import { useEffect, useMemo, useState } from 'react';
import { Plus, Tag, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/textarea';
import { useCreateTask, useTaskSections } from '../../hooks/queries/useTasks';
import { useActiveCollaborators } from '../../hooks/queries/useTaskCollaboration';
import {
  useCreateTaskTag,
  useSetTaskTags,
  useTaskTags,
} from '../../hooks/queries/useTaskTags';
import { useEffectiveTaskFields } from '../../hooks/useEffectiveTaskFields';
import { useAuthStore } from '../../store/useAuthStore';
import { readableTextColor } from '../../lib/colorContrast';
import { applyFicheDueDay } from '../../lib/tasks/taskDueTime';
import { DatePicker } from '../ui/DatePicker';
import { applyTaskTemplate } from '../../lib/tasks/applyTaskTemplate';
import {
  EMPTY_TASK_SECONDARY_QUEUE,
  flushTaskSecondaryQueue,
  type TaskSecondaryQueue,
} from '../../lib/tasks/flushTaskSecondaryQueue';
import { withDefaultSelfAssignee } from '../../lib/tasks/taskAssignmentDefaults';
import { uploadTaskAttachment, addTaskComment } from '../../services/tasks/taskCollaborationService';
import { linkFile } from '../../services/files/taskFilesService';
import type { CollaboratorSummary } from '../../services/tasks/taskCollaborationService';
import type { TaskTemplate } from '../../services/tasks/taskTemplateService';
import { getPriorityMeta } from './taskHelpers';
import { TaskAssigneeDropdown } from './TaskAssigneeDropdown';
import { AssigneeAvatars } from './AssigneeAvatars';
import { TaskTemplatePicker } from './TaskTemplatePicker';
import { TaskSecondaryPendingAccordion } from './TaskSecondaryPendingAccordion';
import { TAG_PALETTE } from './TaskEditModal';

const PRIORITIES = [1, 2, 3, 4] as const;

interface TaskCreateModalProps {
  open: boolean;
  onClose: () => void;
  dossierId: string | null;
  sectionId?: string | null;
}

export function TaskCreateModal({
  open,
  onClose,
  dossierId,
  sectionId: initialSectionId = null,
}: TaskCreateModalProps) {
  const createTask = useCreateTask();
  const setTaskTags = useSetTaskTags();
  const createTag = useCreateTaskTag();
  const fields = useEffectiveTaskFields();
  const { data: allTags = [] } = useTaskTags();
  const { data: sections = [] } = useTaskSections(dossierId);
  const { data: collaborators = [] } = useActiveCollaborators();
  const me = useAuthStore((s) => s.collaborator);

  const [content, setContent] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState(3);
  const [sectionId, setSectionId] = useState<string | null>(initialSectionId);
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [deadline, setDeadline] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());
  const [assigneeIds, setAssigneeIds] = useState<string[]>([]);
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState<string>(TAG_PALETTE[0]!);
  const [queue, setQueue] = useState<TaskSecondaryQueue>(EMPTY_TASK_SECONDARY_QUEUE);
  const [estimatedMinutes, setEstimatedMinutes] = useState<number | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setContent('');
    setDescription('');
    setPriority(3);
    setSectionId(initialSectionId);
    setDueDate('');
    setDueTime('');
    setDeadline('');
    setSelectedTagIds(new Set());
    setShowNewTag(false);
    setNewTagName('');
    setNewTagColor(TAG_PALETTE[0]!);
    setQueue(EMPTY_TASK_SECONDARY_QUEUE);
    setEstimatedMinutes(null);
    setTemplateId(null);
    setAssigneeIds(withDefaultSelfAssignee([], me?.id ?? null));
  }, [open, initialSectionId, me?.id]);

  const assignees = useMemo(
    () => collaborators.filter((c) => assigneeIds.includes(c.id)),
    [collaborators, assigneeIds],
  );

  function toggleTag(id: string) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAssignee(c: CollaboratorSummary) {
    setAssigneeIds((prev) =>
      prev.includes(c.id) ? prev.filter((id) => id !== c.id) : [...prev, c.id],
    );
  }

  async function handleCreateInlineTag() {
    const trimmed = newTagName.trim();
    if (!trimmed) return;
    const created = await createTag.mutateAsync({ name: trimmed, color: newTagColor });
    setSelectedTagIds((prev) => new Set(prev).add(created.id));
    setNewTagName('');
    setNewTagColor(TAG_PALETTE[0]!);
    setShowNewTag(false);
  }

  function pickTemplate(template: TaskTemplate) {
    const applied = applyTaskTemplate(
      {
        content: template.content,
        description: template.description,
        priority: template.priority,
        tagIds: template.tag_ids,
        estimated_duration_minutes: template.estimated_duration_minutes,
      },
      { title: content, parsedPriority: null, parsedTagIds: [], dueDate: dueDate || null },
    );
    setContent(applied.content);
    setDescription(applied.description ?? '');
    if (fields.priority) setPriority(applied.priority);
    if (fields.tags) setSelectedTagIds(new Set(applied.tagIds));
    setEstimatedMinutes(applied.estimated_duration_minutes);
    setTemplateId(template.id);
  }

  async function handleCreate() {
    const trimmed = content.trim();
    if (!trimmed) return;
    setSaving(true);
    const due = dueDate
      ? applyFicheDueDay(dueDate, dueTime || null)
      : { due_date: null, due_datetime: null };
    try {
      const task = await createTask.mutateAsync({
        content: trimmed,
        description: description.trim() || null,
        priority: fields.priority ? priority : 3,
        due_date: due.due_date,
        due_datetime: due.due_datetime,
        deadline: deadline || null,
        dossier_id: dossierId,
        section_id: fields.sections ? sectionId : null,
        estimated_duration_minutes: estimatedMinutes,
        assigneeIds,
        task_template_id: templateId,
      });
      if (fields.tags && selectedTagIds.size > 0) {
        try {
          await setTaskTags.mutateAsync({ taskId: task.id, tagIds: Array.from(selectedTagIds) });
        } catch {
          toast.error('Tâche créée, mais les étiquettes n’ont pas été appliquées.');
        }
      }
      const warnings = await flushTaskSecondaryQueue(task.id, queue, {
        upload: (id, file) => uploadTaskAttachment(id, file),
        linkFile,
        addComment: (id, text) => addTaskComment(id, text),
        linkedBy: me?.id ?? null,
      });
      if (warnings.length > 0) {
        toast.error(`Tâche créée, mais ${warnings.join(' ; ')}.`);
      } else {
        toast.success('Tâche créée');
      }
      onClose();
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Impossible de créer la tâche',
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent className="max-w-3xl bg-white p-0 gap-0 [&>button]:hidden">
        <div className="flex items-start gap-3 px-6 py-4 border-b border-neutral-200">
          <div className="flex-1 min-w-0">
            <DialogTitle asChild>
              <input
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full text-lg font-semibold bg-transparent border-0 outline-none focus:bg-neutral-50 rounded px-1 -mx-1 text-cime-text-primary"
                style={{ fontFamily: 'var(--cime-font-heading)' }}
                placeholder="Titre de la tâche"
              />
            </DialogTitle>
            <div className="mt-2">
              <TaskTemplatePicker onSelect={pickTemplate} />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-cime-text-primary p-1"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="flex items-center justify-between gap-3 -mt-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide">
                Intervenants
              </span>
              {assignees.length > 0 ? (
                <AssigneeAvatars assignees={assignees} size="xs" max={6} />
              ) : (
                <span className="text-xs text-cime-text-muted italic">aucun</span>
              )}
            </div>
            <TaskAssigneeDropdown
              assignees={assignees}
              onLocalToggle={toggleAssignee}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
              Description
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Ajouter une description, un contexte, des liens…"
              rows={4}
              className="resize-none"
            />
          </div>

          {(fields.sections || fields.priority) && (
            <div className="grid grid-cols-2 gap-4">
              {fields.sections && (
                <div>
                  <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
                    Section
                  </label>
                  <select
                    value={sectionId ?? ''}
                    onChange={(e) => setSectionId(e.target.value || null)}
                    className="w-full text-sm border border-neutral-200 rounded-md px-2 py-2 bg-white outline-none focus:border-cime-primary"
                  >
                    <option value="">— Sans section —</option>
                    {sections.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {fields.priority && (
                <div>
                  <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
                    Priorité
                  </label>
                  <div className="inline-flex items-center rounded-md border border-neutral-200 p-0.5">
                    {PRIORITIES.map((p) => {
                      const meta = getPriorityMeta(p);
                      const isActive = priority === p;
                      return (
                        <button
                          key={p}
                          type="button"
                          onClick={() => setPriority(p)}
                          className={`px-2.5 py-1 rounded text-xs font-medium transition ${
                            isActive ? meta.className : 'text-cime-text-muted hover:bg-neutral-50'
                          }`}
                        >
                          {meta.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
                Échéance
              </label>
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                showTime
                time={dueTime}
                onTimeChange={setDueTime}
                aria-label="Échéance"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide block mb-1.5">
                Date de relance
              </label>
              <DatePicker
                value={deadline}
                onChange={setDeadline}
                aria-label="Date de relance"
              />
            </div>
          </div>

          {fields.tags && (
            <div className="pt-2 border-t border-neutral-100">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide flex items-center gap-1.5">
                  <Tag className="w-3.5 h-3.5" />
                  Étiquettes
                </label>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {allTags.map((tag) => {
                  const on = selectedTagIds.has(tag.id);
                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleTag(tag.id)}
                      className={`text-xs px-2 py-0.5 rounded-full border transition ${
                        on ? '' : 'text-neutral-600 bg-white hover:bg-neutral-50'
                      }`}
                      style={
                        on
                          ? {
                              backgroundColor: tag.color,
                              borderColor: tag.color,
                              color: readableTextColor(tag.color),
                            }
                          : { borderColor: tag.color }
                      }
                    >
                      {tag.name}
                    </button>
                  );
                })}
                {!showNewTag && (
                  <button
                    type="button"
                    onClick={() => setShowNewTag(true)}
                    className="text-xs px-2 py-0.5 rounded-full border border-dashed border-neutral-300 text-neutral-500 hover:border-cime-primary hover:text-cime-primary transition inline-flex items-center gap-1"
                  >
                    <Plus className="w-3 h-3" /> Nouvelle étiquette
                  </button>
                )}
              </div>
              {showNewTag && (
                <div className="mt-2 p-2.5 rounded-md border border-neutral-200 bg-neutral-50 space-y-2">
                  <Input
                    autoFocus
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="Nom de l’étiquette"
                    maxLength={50}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        void handleCreateInlineTag();
                      }
                      if (e.key === 'Escape') setShowNewTag(false);
                    }}
                  />
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex gap-1">
                      {TAG_PALETTE.map((c) => (
                        <button
                          key={c}
                          type="button"
                          onClick={() => setNewTagColor(c)}
                          className={`w-5 h-5 rounded-full border-2 ${
                            newTagColor === c ? 'border-neutral-900' : 'border-transparent'
                          }`}
                          style={{ backgroundColor: c }}
                          aria-label={`Couleur ${c}`}
                        />
                      ))}
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowNewTag(false)}>
                        Annuler
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        disabled={!newTagName.trim() || createTag.isPending}
                        onClick={() => void handleCreateInlineTag()}
                      >
                        <Plus className="w-3.5 h-3.5" /> Créer
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <TaskSecondaryPendingAccordion
            dossierId={dossierId}
            queue={queue}
            onChange={setQueue}
          />
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-neutral-200 bg-neutral-50 rounded-b-lg">
          <Button variant="ghost" onClick={onClose} size="sm">
            Annuler
          </Button>
          <Button
            onClick={() => void handleCreate()}
            size="sm"
            disabled={!content.trim() || saving || createTask.isPending}
          >
            {saving ? 'Création…' : 'Créer'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
